import logger from 'loglevel';
import { siUnitFormat } from '../util.js';
interface Performance {
    now(): number;
}

let performance = { now: Date.now };

/**
 * Allow consumer to configure the performance singleton for these classes. Browser and Server
 * have different ways to get the performance singleton. If neither is used then Date.now is used.
 * @param newPerformance Object that conforms to the Performance interface
 */
export function setCounterPerformance(newPerformance: Performance) {
    performance = newPerformance;
}

export interface GenericCounterAccumulator<C extends CounterState, D extends CounterSnapshotState> {
    current: C[];
    delta?: D[];
    snapshotMap?: Map<string, D>;
    timestamp: number;
    deltaTime?: number;
}
export type CounterAccumulator = GenericCounterAccumulator<Counter, CounterSnapshot>;
export type CounterAccumulatorState = GenericCounterAccumulator<CounterState, CounterSnapshotState>;

export interface CounterState {
    name: string;
    total: number;
    min: number | null;
    max: number | null;
    deltaMin: number | null;
    deltaMax: number | null;
    count: number;
    units: CounterUnits;
}

export enum CounterUnits {
    Milliseconds = 'ms',
    Bytes = 'b',
    Unit = 'x',
    Percent = '%'
}

const MS_TO_S = 1000;
const DEFAULT_COUNTER_UNITS = CounterUnits.Milliseconds;

function min(a: number | null, b: number | null) {
    if (a === null) {
        return b;
    } else if (b === null) {
        return a;
    } else {
        return Math.min(a, b);
    }
}

function max(a: number | null, b: number | null) {
    if (a === null) {
        return b;
    } else if (b === null) {
        return a;
    } else {
        return Math.max(a, b);
    }
}

abstract class BaseCounter implements CounterState {
    total: number = 0;
    min: number | null = null;
    max: number | null = null;
    deltaMin: number | null = null;
    deltaMax: number | null = null;
    count: number = 0;
    timestamp: number = performance.now();

    constructor(
        public name: string,
        public units = DEFAULT_COUNTER_UNITS
    ) {}

    print(withTotal = false) {
        return `${Counter.fmtUnits(this.avg(), this.units)} avg ${this.name} (${this.fmtNum(
            this.count
        )}x)${withTotal ? ` ${Counter.fmtUnits(this.total, this.units)}` : ''}`;
    }

    avg() {
        if (this.count > 0) {
            return this.total / this.count;
        }
        return 0;
    }

    fmtNum(value: number) {
        return value.toFixed(2).replace(/\.?0+$/, '');
    }
}

export interface CounterSnapshotState extends CounterState {
    interval: number | null;
    timestamp: number | null;
}

/**
 * Snapshot of a counter including the interval since the last snapshot and other information
 */
export class CounterSnapshot extends BaseCounter implements Readonly<CounterSnapshotState> {
    /**
     * If counter is a Counter or delta is true then include the intervals since the last snapshot.
     * @param counter Counter or snapshot to create a snapshot of
     * @param delta If delta is true then use the other snapshot's timestamp to create the interval.
     */
    constructor(counter: Counter | CounterSnapshotState, delta = false) {
        super(counter.name, counter.units);
        this.name = counter.name;
        this.units = counter.units;
        this.total = counter.total;
        this.count = counter.count;
        this.min = counter.min;
        this.max = counter.max;
        if (counter instanceof Counter || delta) {
            this.timestamp = performance.now();
            if (counter instanceof Counter) {
                this.interval = 0;
            } else {
                this.interval = counter.timestamp ? this.timestamp - counter.timestamp : 0;
            }
        } else {
            this.timestamp = counter.timestamp || 0;
            this.interval = counter.interval || 0;
        }
    }

    readonly name: string;
    readonly units: CounterUnits;
    readonly total: number;
    readonly count: number;
    readonly min: number | null;
    readonly max: number | null;
    readonly deltaMin: number | null = null;
    readonly deltaMax: number | null = null;
    readonly interval: number;
    readonly timestamp: number;
    private _values: string[] | null = null;

    perSecond() {
        return this.interval > 0 ? this.total / (this.interval / MS_TO_S) : 0;
    }

    get values(): string[] {
        if (!this._values) {
            this._values = [
                `${Counter.fmtUnits(this.avg(), this.units)} avg`,
                this.name,
                `${this.fmtNum(this.count)}x`,
                Counter.fmtUnits(this.total, this.units),
                Counter.fmtUnits(this.perSecond(), this.units, true)
            ];
        }
        return this._values;
    }
}
export class Counter extends BaseCounter {
    private _values: string[] | null = null;

    static fromState(state: CounterState) {
        const result = new Counter(state.name, state.units);
        result.total = state.total;
        result.count = state.count;
        result.min = state.min;
        result.max = state.max;
        result.deltaMin = state.deltaMin;
        result.deltaMax = state.deltaMax;
        return result;
    }
    static accFromState(state: CounterAccumulatorState): CounterAccumulator {
        if (!state.current) {
            logger.error('state needs current');
        }
        return {
            ...state,
            current: state.current.map(c => Counter.fromState(c)),
            delta: state.delta?.map(d => new CounterSnapshot(d)),
            snapshotMap: undefined
        };
    }
    static topAvg(a: Counter | CounterSnapshot, b: Counter | CounterSnapshot) {
        return b.avg() - a.avg();
    }
    static topTotal(a: CounterState, b: CounterState) {
        return b.total - a.total;
    }

    static fmtUnits(value: number, counterUnits = DEFAULT_COUNTER_UNITS, perSecond = false) {
        if (typeof value !== 'number') {
            return value;
        }
        let perSecondStr = perSecond ? '/s' : '';
        let units: string = counterUnits;
        if (value) {
            if (counterUnits === CounterUnits.Milliseconds) {
                if (value > 1000) {
                    value /= 1000;
                    units = 's';
                    if (value > 60) {
                        value /= 60;
                        units = 'min';
                    }
                }
            } else if (counterUnits === CounterUnits.Bytes) {
                const { value: nv, unit } = siUnitFormat(value);
                value = nv;
                units = unit;
            } else {
                if (counterUnits !== CounterUnits.Unit) {
                    logger.warn(`Unit not supported: ${counterUnits}`);
                }
                return `${value}${counterUnits}`;
            }
            return `${value.toFixed(2).replace(/\.?0+$/, '')}${units}${perSecondStr}`;
        }
        return '';
    }
    constructor(name: string, units?: CounterUnits) {
        super(name, units);
    }
    merge(other: Counter | number) {
        this._values = null;
        if (other instanceof Counter) {
            this.total += other.total;
            this.count += other.count;
            this.max = max(this.max, other.max);
            this.min = min(this.min, other.min);
            this.deltaMax = null;
            this.deltaMin = null;
        } else {
            this.max = max(this.max, other);
            this.min = min(this.min, other);
            this.deltaMax = max(this.deltaMax, other);
            this.deltaMin = min(this.deltaMin, other);
            this.total += other;
            this.count += 1;
        }
    }

    delta(other?: CounterSnapshot) {
        try {
            if (!other) {
                return this.snapshot();
            }
            return new CounterSnapshot(
                {
                    name: this.name,
                    units: this.units,
                    total: this.total - other.total,
                    count: this.count - other.count,
                    // cheating here, we just assume that the min/max is for the interval since the last delta.
                    min: this.deltaMin,
                    max: this.deltaMax,
                    deltaMax: other.deltaMax,
                    deltaMin: other.deltaMin,
                    timestamp: other.timestamp,
                    interval: 0
                },
                true
            );
        } finally {
            // unset delta min/max...
            // this way we will accumulate new delta min/max values during the delta
            this.deltaMin = null;
            this.deltaMax = null;
        }
    }

    get values() {
        if (!this._values) {
            this._values = [
                `${Counter.fmtUnits(this.avg(), this.units)} avg`,
                this.name,
                `${this.fmtNum(this.count)}x`,
                Counter.fmtUnits(this.total, this.units)
            ];
        }
        return this._values;
    }

    /**
     * Create a snapshot of the counter and update the timestamp
     * @returns A snapshot of the counter including the interval for the values.
     */
    snapshot() {
        const c = new Counter(this.name, this.units);
        c.total = this.total;
        c.count = this.count;
        c.min = this.min;
        c.max = this.max;
        c.deltaMin = this.deltaMin;
        c.deltaMax = this.deltaMax;
        return new CounterSnapshot(c);
    }
}
